import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Grid,
  Paper,
  Stack,
  styled,
  Typography,
} from "~/common/mui";
import { C_OUTLINE_VARIANT, C_PRIMARY_CONTAINER, C_SURFACE_BRIGHT, C_WHITE } from "~/common/components/icon/Colors";
import { RotatingWidgets } from "~/features/cms/homepagePersonalization/components/RotatingWidget";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import OrderTable, { TableType } from "~/features/cms/homepagePersonalization/components/OrderTable";
import { useTitleOptions } from "~/domains/referenceData/hooks/useFormOptions";
import { type ReactNode, useEffect, useState } from "react";
import { userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import { useSelector } from "react-redux";
import { selectMinicartData } from "~/features/cart/minicart/minicartRedux";
import { CART_PAGE, ORDER_HISTORY_PAGE, QUOTE_HISTORY_PAGE } from "~/utils/url/staticUrls";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import type { CMSHomepagePersonalizationDTO } from "~/features/cms/types/dto";
import { useCMSComponentRenderer } from "~/features/cms/CMSComponentRendererContext";
import { useTranslation } from "react-i18next";
import { IMG_EMPTY_CART } from "~/utils/url/staticResources";
import { fetchQuoteHistory } from "~/features/orders/quoteList/api/fetchQuoteHistory/fetchQuoteHistory";
import { fetchOrderHistory } from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";
import type { OrderData } from "~/domains/orders/types/modelsV2";
import BusinessIcon from "@mui/icons-material/Business";
import dayjs from "dayjs";
import CPD from "~/features/cms/homepagePersonalization/cpd/CPD";
import TableSkeleton from "~/features/cms/homepagePersonalization/components/loadingSkeleton/TableSkeleton";
import { format } from "~/common/utils/ApiHelper";
import { useCommerceContext } from "~/app/config/useSession";
import type { SelectedAccountData } from "~/domains/user/types/SessionUserData";
import { useCustomerPerformanceDashboard } from "~/features/cms/homepagePersonalization/cpd/customerPerformanceApi";

interface Props extends Omit<CMSHomepagePersonalizationDTO, "type"> {
  className?: string;
}

export const CMSHomepagePersonalization = (props: Props) => {
  const renderCMSComponent = useCMSComponentRenderer();
  const { chipsComponent, rotatingComponent, recentComponents, title } = props;
  const { t } = useTranslation();

  const isDesktop = useIsMinDesktopSize();

  const { val: cartView, setFalse: hideCartView } = useBooleanState(
    window.sessionStorage.getItem("homeCartHidden") !== "true",
  );
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [quotes, setQuotes] = useState<OrderData[]>([]);

  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const { count } = useSelector(selectMinicartData);
  const commerceContext = useCommerceContext();
  const selectedAccount = commerceContext?.selectedAccount;

  const titleOptions = useTitleOptions();

  const isToggleDisabled =
    !commerceContext?.homepagePersonalizationComponentEnabled ||
    userIsPublic() ||
    userIsRegistered() ||
    !selectedAccount;
  const selectedAccountKey = selectedAccount
    ? [selectedAccount.uid, selectedAccount.salesOrg, selectedAccount.endCustomerCode ?? ""].join("|")
    : "";
  const { data: customerPerformanceDashboard } = useCustomerPerformanceDashboard();

  useEffect(() => {
    setOrders([]);
    setQuotes([]);
    if (isToggleDisabled || recentComponents.length === 0) {
      setLoadingOrders(false);
      setLoadingQuotes(false);
      return;
    }

    let active = true;
    const hasOrders = recentComponents.some((comp) => comp.componentType === TableType.ORDERS);
    const hasQuotes = recentComponents.some((comp) => comp.componentType === TableType.QUOTES);

    if (hasOrders) {
      setLoadingOrders(true);
      fetchHistory(TableType.ORDERS, selectedAccount)
        .then((result) => active && setOrders(result))
        .finally(() => active && setLoadingOrders(false));
    }

    if (hasQuotes) {
      setLoadingQuotes(true);
      fetchHistory(TableType.QUOTES, selectedAccount)
        .then((result) => active && setQuotes(result))
        .finally(() => active && setLoadingQuotes(false));
    }

    return () => {
      active = false;
    };
  }, [isToggleDisabled, recentComponents, selectedAccount, selectedAccountKey]);

  if (isToggleDisabled) {
    return null;
  }

  function getLabelByValue(value: string): ReactNode {
    const found = titleOptions.find((t) => t.value === value);
    return found ? found.label : null;
  }

  const showCartTodo = cartView && count > 0;

  const header = (
    <HeaderContainer>
      <Typography variant="headlineSmall">
        {t("homepage.welcome.message")} <b>{title}</b>
      </Typography>
      <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
        <BusinessIcon fontSize="small" />
        <Typography variant="labelLarge">{`${selectedAccount?.name ?? ""} | ${
          selectedAccount?.displayUid ?? ""
        }`}</Typography>
      </Stack>
    </HeaderContainer>
  );

  const cartInProgress = (
    <CartInProgress>
      <ImageContainer>
        <ImageCircle>
          <EmptyCartImage src={IMG_EMPTY_CART} />
        </ImageCircle>
      </ImageContainer>
      <CartContent>
        <Stack>
          <Typography variant="titleMedium">{t("homepage.cart.title")}</Typography>
          <Typography>{t("homepage.cart.subtitle")}</Typography>
        </Stack>
        <Stack direction={"row"} gap={1}>
          <Button
            variant="text"
            onClick={() => {
              window.sessionStorage.setItem("homeCartHidden", "true");
              hideCartView();
            }}
          >
            {t("homepage.cart.dismiss")}
          </Button>
          <Button
            onClick={() => {
              redirectToUrl(CART_PAGE);
            }}
          >
            {t("homepage.cart.continue")}
          </Button>
        </Stack>
      </CartContent>
    </CartInProgress>
  );

  const isCPDDisabled = !customerPerformanceDashboard;

  return (
    <StyledCard>
      <Stack gap={3} justifyContent={"flex-start"}>
        {header}
        {isDesktop && (
          <>
            {chipsComponent.length > 0 && (
              <ChipContainer>
                {chipsComponent.map((chip, index) => {
                  return renderCMSComponent(chip, undefined, index);
                })}
              </ChipContainer>
            )}
            {showCartTodo && cartInProgress}
          </>
        )}
        {!isCPDDisabled && (
          <Grid container spacing={3}>
            <CPD data={customerPerformanceDashboard} />
          </Grid>
        )}
        <Grid container spacing={3}>
          {(rotatingComponent?.banners.length ?? 0) > 0 && (
            <Grid size={12}>
              <Card>
                <StyledAccordion defaultExpanded>
                  <AccordionSummary>
                    <Typography>{t("homepage.news.title")}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RotatingWidgets banners={rotatingComponent.banners} timeout={rotatingComponent.timeout} />
                  </AccordionDetails>
                </StyledAccordion>
              </Card>
            </Grid>
          )}
          {isDesktop &&
            recentComponents.map((comp) => {
              if (
                (comp.componentType === TableType.ORDERS && loadingOrders) ||
                (comp.componentType === TableType.QUOTES && loadingQuotes)
              ) {
                return (
                  <Grid size={isDesktop ? 6 : 12}>
                    <TableSkeleton />
                  </Grid>
                );
              }

              if (
                (comp.componentType === TableType.ORDERS && orders.length === 0) ||
                (comp.componentType === TableType.QUOTES && quotes.length === 0)
              ) {
                return null;
              }

              return (
                <Grid size={isDesktop ? 6 : 12}>
                  <Card>
                    <StyledAccordion defaultExpanded>
                      <AccordionSummary>
                        <AccordionHeader>
                          <Typography>{comp.label}</Typography>
                          <Button
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              const pageURL =
                                comp.componentType === TableType.ORDERS ? ORDER_HISTORY_PAGE : QUOTE_HISTORY_PAGE;
                              redirectToUrl(pageURL);
                            }}
                          >
                            {t("homepage.recent.viewAll")}
                          </Button>
                        </AccordionHeader>
                      </AccordionSummary>
                      <AccordionDetails>
                        <OrderTable
                          type={comp.componentType}
                          data={comp.componentType === TableType.ORDERS ? orders : quotes}
                        />
                      </AccordionDetails>
                    </StyledAccordion>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      </Stack>
    </StyledCard>
  );
};

export const fetchHistory = async (type: TableType, selectedAccount: SelectedAccountData | null | undefined) => {
  const requestData = {
    account: {
      code: selectedAccount?.uid ?? "",
      salesOrg: selectedAccount?.salesOrg ?? "",
      endCustomerCodes: [""],
    },
    customerOrderId: "",
    sdCreationStart: format(dayjs().add(-1, "year").startOf("year").toDate()),
    sdLineStatuses: [],
    sdStatus: "",
    recentOrdersRequest: true,
  };

  if (type === TableType.QUOTES) {
    const res = await fetchQuoteHistory({ data: requestData });
    return res.data.results;
  } else {
    const res = await fetchOrderHistory({ data: requestData });
    return res.data.results;
  }
};

const StyledCard = styled(Paper)(({ theme }) => ({
  width: "100vw",
  position: "relative",
  left: "50%",
  right: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",

  [theme.breakpoints.up("tablet")]: {
    margin: "16px 0",
    width: "unset",
    left: "unset",
    right: "unset",
  },

  padding: "16px",
  backgroundColor: C_SURFACE_BRIGHT,
}));

const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("tablet")]: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "8px",
  },
}));

const ChipContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: "8px",
}));

const ImageContainer = styled("div")(({ theme }) => ({
  width: "80px",
  height: "80px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: C_PRIMARY_CONTAINER,
  flexShrink: 0,
}));

const EmptyCartImage = styled("img")(({ theme }) => ({
  height: "32px",
}));

const CartContent = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexGrow: 1,
  padding: "0 16px",
  backgroundColor: C_WHITE,
}));

const ImageCircle = styled("div")(({ theme }) => ({
  backgroundColor: "white",
  width: "68px",
  height: "68px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
}));

const CartInProgress = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderRadius: "8px",
  overflow: "hidden",
}));

const StyledAccordion = styled(Accordion)({
  ["&:before"]: {
    display: "none !important",
  },
  ["&:after"]: {
    display: "none !important",
  },
});

const AccordionHeader = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));
